import type { ShapeKind } from '../hooks/useStamps';

/**
 * 形狀印章共用渲染工具：
 * - renderShapeToSvgDataUrl: 產生 SVG dataURL（給 stamp 顯示用 <img>）
 * - drawShapeOnCanvas: 直接在 canvas context 上繪製（給 composeImage 用）
 */

// ── SVG dataURL ────────────────────────────────────────────────────────────
// 預覽 / 顯示用。使用 vector-effect="non-scaling-stroke" 在 HTML 中能保持線條粗細
const HEART_PATH = 'M50 85 C 5 60, -5 25, 25 12 C 38 6, 50 18, 50 30 C 50 18, 62 6, 75 12 C 105 25, 95 60, 50 85 Z';
const STAR_PATH = 'M50 8 L61 38 L93 38 L67 57 L77 88 L50 70 L23 88 L33 57 L7 38 L39 38 Z';

const renderInnerSvg = (kind: ShapeKind, color: string, lineWidth: number, filled: boolean): string => {
  const lw = Math.max(2, lineWidth);
  const fillAttr = filled ? `fill="${color}"` : 'fill="none"';
  const strokeAttr = filled ? '' : `stroke="${color}" stroke-width="${lw}" stroke-linejoin="round" vector-effect="non-scaling-stroke"`;
  switch (kind) {
    case 'rect':
      return filled
        ? `<rect width="100" height="100" fill="${color}"/>`
        : `<rect x="${lw / 2}" y="${lw / 2}" width="${100 - lw}" height="${100 - lw}" fill="none" stroke="${color}" stroke-width="${lw}" vector-effect="non-scaling-stroke"/>`;
    case 'circle':
      return filled
        ? `<circle cx="50" cy="50" r="50" fill="${color}"/>`
        : `<ellipse cx="50" cy="50" rx="${(100 - lw) / 2}" ry="${(100 - lw) / 2}" fill="none" stroke="${color}" stroke-width="${lw}" vector-effect="non-scaling-stroke"/>`;
    case 'heart':
      return `<path d="${HEART_PATH}" ${fillAttr} ${strokeAttr}/>`;
    case 'star':
      return `<path d="${STAR_PATH}" ${fillAttr} ${strokeAttr}/>`;
  }
};

export const renderShapeToSvgDataUrl = (
  kind: ShapeKind, color: string, lineWidth: number, filled: boolean,
): string => {
  const inner = renderInnerSvg(kind, color, lineWidth, filled);
  // 加上 width/height 確保所有瀏覽器把 intrinsic 視為 1:1（沒設的話部分瀏覽器會用 300×150 預設值）
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100" preserveAspectRatio="none">${inner}</svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

// ── Canvas drawing（合成圖時用） ───────────────────────────────────────────
// 用 path 直接畫在 ctx 上，stroke / fill 都正確不受 raster scale 影響

const tracePath = (ctx: CanvasRenderingContext2D, kind: ShapeKind, x: number, y: number, w: number, h: number) => {
  const cx = x + w / 2;
  const cy = y + h / 2;
  switch (kind) {
    case 'rect': {
      ctx.beginPath();
      ctx.rect(x, y, w, h);
      break;
    }
    case 'circle': {
      ctx.beginPath();
      ctx.ellipse(cx, cy, w / 2, h / 2, 0, 0, Math.PI * 2);
      break;
    }
    case 'heart': {
      // 將 HEART_PATH (viewBox 0..100) 縮放到 x/y/w/h
      const sx = (px: number) => x + (px / 100) * w;
      const sy = (py: number) => y + (py / 100) * h;
      ctx.beginPath();
      ctx.moveTo(sx(50), sy(85));
      ctx.bezierCurveTo(sx(5), sy(60), sx(-5), sy(25), sx(25), sy(12));
      ctx.bezierCurveTo(sx(38), sy(6), sx(50), sy(18), sx(50), sy(30));
      ctx.bezierCurveTo(sx(50), sy(18), sx(62), sy(6), sx(75), sy(12));
      ctx.bezierCurveTo(sx(105), sy(25), sx(95), sy(60), sx(50), sy(85));
      ctx.closePath();
      break;
    }
    case 'star': {
      // 5-pointed star，coords 從 STAR_PATH 取出
      const points = [
        [50, 8], [61, 38], [93, 38], [67, 57], [77, 88],
        [50, 70], [23, 88], [33, 57], [7, 38], [39, 38],
      ];
      ctx.beginPath();
      points.forEach(([px, py], i) => {
        const X = x + (px / 100) * w;
        const Y = y + (py / 100) * h;
        if (i === 0) ctx.moveTo(X, Y);
        else ctx.lineTo(X, Y);
      });
      ctx.closePath();
      break;
    }
  }
};

export const drawShapeOnCanvas = (
  ctx: CanvasRenderingContext2D,
  kind: ShapeKind,
  color: string,
  lineWidth: number,
  filled: boolean,
  x: number, y: number, w: number, h: number,
  /** stroke 粗細乘上的縮放因子（讓預覽縮小後仍可見） */
  strokeFactor = 3,
) => {
  if (filled) {
    ctx.fillStyle = color;
    tracePath(ctx, kind, x, y, w, h);
    ctx.fill();
  } else {
    const lw = Math.max(2, lineWidth) * strokeFactor;
    ctx.strokeStyle = color;
    ctx.lineWidth = lw;
    ctx.lineJoin = 'round';
    // 框線往內縮 lw/2 避免被裁切
    tracePath(ctx, kind, x + lw / 2, y + lw / 2, Math.max(1, w - lw), Math.max(1, h - lw));
    ctx.stroke();
  }
};
